import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Image, GripVertical, Eye, EyeOff, Upload, X, Monitor, Smartphone } from "lucide-react";
import { supabase } from "@/supabase";
import { useToast } from "@/hooks/use-toast";
import AdminLayout from "@/components/admin/AdminLayout";
import AuthGuard from "@/components/admin/AuthGuard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { uploadCarousels } from "@/hooks/uploadImages";

interface CarouselSlide {
  id: string;
  title: string;
  subtitle: string | null;
  image_url: string | null;
  image_url_mobile?: string | null; // Tornar opcional para evitar erro de tipo
  button_text: string | null;
  button_link: string | null;
  whatsapp_text: string | null;
  is_active: boolean;
  display_order: number;
}

const AdminCarousel = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState<CarouselSlide | null>(null);
  const [isUploadingDesktop, setIsUploadingDesktop] = useState(false);
  const [isUploadingMobile, setIsUploadingMobile] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    image_url: "",
    image_url_mobile: "",
    button_text: "Comprar agora",
    button_link: "#produtos",
    whatsapp_text: "Falar no WhatsApp",
    is_active: true,
    display_order: 0,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: slides, isLoading } = useQuery({
    queryKey: ["admin-carousel"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("carousel_slides")
        .select("*")
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data as CarouselSlide[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: { title: string; subtitle?: string | null; image_url?: string | null; image_url_mobile?: string | null; button_text?: string | null; button_link?: string | null; whatsapp_text?: string | null; is_active?: boolean; display_order?: number }) => {
      const { error } = await supabase.from("carousel_slides").insert([data]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-carousel"] });
      toast({ title: "Slide criado com sucesso!" });
      closeDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Erro ao criar slide", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CarouselSlide> }) => {
      const { error } = await supabase.from("carousel_slides").update(data).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-carousel"] });
      toast({ title: "Slide atualizado com sucesso!" });
      closeDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Erro ao atualizar slide", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("carousel_slides").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-carousel"] });
      toast({ title: "Slide excluído com sucesso!" });
    },
    onError: (error: Error) => {
      toast({ title: "Erro ao excluir slide", description: error.message, variant: "destructive" });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from("carousel_slides").update({ is_active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-carousel"] });
      toast({ title: "Visibilidade atualizada!" });
    },
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'desktop' | 'mobile') => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Erro",
        description: "Por favor, selecione apenas arquivos de imagem.",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Erro",
        description: "A imagem deve ter no máximo 10MB.",
        variant: "destructive",
      });
      return;
    }

    try {
      if (type === 'desktop') {
        setIsUploadingDesktop(true);
      } else {
        setIsUploadingMobile(true);
      }

      const imageUrl = await uploadCarousels(file);
      
      if (type === 'desktop') {
        setFormData({ ...formData, image_url: imageUrl });
      } else {
        setFormData({ ...formData, image_url_mobile: imageUrl });
      }

      toast({
        title: "Sucesso",
        description: `Imagem ${type === 'desktop' ? 'desktop' : 'mobile'} enviada com sucesso!`,
      });
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      toast({
        title: "Erro ao fazer upload",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      if (type === 'desktop') {
        setIsUploadingDesktop(false);
      } else {
        setIsUploadingMobile(false);
      }
    }
  };

  const removeImage = (type: 'desktop' | 'mobile') => {
    if (type === 'desktop') {
      setFormData({ ...formData, image_url: "" });
    } else {
      setFormData({ ...formData, image_url_mobile: "" });
    }
  };

  const openDialog = (slide?: CarouselSlide) => {
    if (slide) {
      setEditingSlide(slide);
      setFormData({
        title: slide.title,
        subtitle: slide.subtitle || "",
        image_url: slide.image_url || "",
        image_url_mobile: slide.image_url_mobile || "",
        button_text: slide.button_text || "Comprar agora",
        button_link: slide.button_link || "#produtos",
        whatsapp_text: slide.whatsapp_text || "Falar no WhatsApp",
        is_active: slide.is_active,
        display_order: slide.display_order,
      });
    } else {
      setEditingSlide(null);
      setFormData({
        title: "",
        subtitle: "",
        image_url: "",
        image_url_mobile: "",
        button_text: "Comprar agora",
        button_link: "#produtos",
        whatsapp_text: "Falar no WhatsApp",
        is_active: true,
        display_order: (slides?.length || 0) + 1,
      });
    }
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingSlide(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const slideData = {
      title: formData.title,
      subtitle: formData.subtitle || null,
      image_url: formData.image_url || null,
      image_url_mobile: formData.image_url_mobile || null,
      button_text: formData.button_text || null,
      button_link: formData.button_link || null,
      whatsapp_text: formData.whatsapp_text || null,
      is_active: formData.is_active,
      display_order: formData.display_order,
    };

    if (editingSlide) {
      updateMutation.mutate({ id: editingSlide.id, data: slideData });
    } else {
      createMutation.mutate(slideData);
    }
  };

  return (
    <AuthGuard>
      <AdminLayout>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-semibold text-foreground">
              Carrossel
            </h1>
            <p className="text-muted-foreground mt-1">
              Gerencie os banners do carrossel da página inicial
            </p>
          </div>
          <button onClick={() => openDialog()} className="btn-primary flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Novo Slide
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground">Carregando...</div>
        ) : slides && slides.length > 0 ? (
          <div className="space-y-4">
            {slides.map((slide) => (
              <div
                key={slide.id}
                className={`admin-card flex items-center gap-4 ${
                  !slide.is_active ? "opacity-60" : ""
                }`}
              >
                <GripVertical className="w-5 h-5 text-muted-foreground cursor-grab" />

                <div className="w-32 h-20 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                  {slide.image_url ? (
                    <img
                      src={slide.image_url}
                      alt={slide.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-8 h-8 text-muted-foreground" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground truncate">{slide.title}</h3>
                  {slide.subtitle && (
                    <p className="text-sm text-muted-foreground truncate">{slide.subtitle}</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      toggleActiveMutation.mutate({ id: slide.id, is_active: !slide.is_active })
                    }
                    className={`p-2 rounded-lg transition-colors ${
                      slide.is_active
                        ? "bg-green-100 text-green-600"
                        : "bg-muted text-muted-foreground"
                    }`}
                    title={slide.is_active ? "Desativar" : "Ativar"}
                  >
                    {slide.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => openDialog(slide)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                    title="Editar"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm("Tem certeza que deseja excluir este slide?")) {
                        deleteMutation.mutate(slide.id);
                      }
                    }}
                    className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                    title="Excluir"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="admin-card text-center py-12">
            <Image className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">Nenhum slide cadastrado ainda.</p>
            <button onClick={() => openDialog()} className="btn-primary">
              Adicionar primeiro slide
            </button>
          </div>
        )}

        {/* Slide Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">
                {editingSlide ? "Editar Slide" : "Novo Slide"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="admin-label">Título *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="admin-input"
                  required
                />
              </div>
              <div>
                <label className="admin-label">Subtítulo</label>
                <textarea
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  className="admin-input min-h-[80px]"
                  rows={3}
                />
              </div>

              {/* Imagem Desktop */}
              <div className="space-y-3 border-t pt-4">
                <label className="admin-label flex items-center gap-2 font-medium">
                  <Monitor className="w-4 h-4" />
                  Imagem Desktop (Recomendado: 1920x600px)
                </label>

                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">URL da Imagem Desktop</label>
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="admin-input"
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Ou faça upload de uma imagem desktop</label>
                  <div className="space-y-3">
                    {formData.image_url && (
                      <div className="relative w-full h-48 bg-muted rounded-lg overflow-hidden">
                        <img
                          src={formData.image_url}
                          alt="Preview Desktop"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage('desktop')}
                          className="absolute top-2 right-2 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        id="image-upload-desktop"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'desktop')}
                        disabled={isUploadingDesktop}
                        className="hidden"
                      />
                      <label
                        htmlFor="image-upload-desktop"
                        className={`flex items-center justify-center gap-2 px-4 py-2 border border-border rounded-lg cursor-pointer hover:bg-muted transition-colors ${
                          isUploadingDesktop ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        <Upload className="w-4 h-4" />
                        {isUploadingDesktop ? "Enviando..." : "Escolher imagem desktop"}
                      </label>
                      {formData.image_url && (
                        <span className="text-xs text-muted-foreground">
                          Imagem configurada
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Tamanho máximo: 10MB. Formatos: JPG, PNG, WebP
                    </p>
                  </div>
                </div>
              </div>

              {/* Imagem Mobile */}
              <div className="space-y-3 border-t pt-4">
                <label className="admin-label flex items-center gap-2 font-medium">
                  <Smartphone className="w-4 h-4" />
                  Imagem Mobile (Recomendado: 800x1000px)
                </label>

                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">URL da Imagem Mobile</label>
                  <input
                    type="url"
                    value={formData.image_url_mobile}
                    onChange={(e) => setFormData({ ...formData, image_url_mobile: e.target.value })}
                    className="admin-input"
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Ou faça upload de uma imagem mobile</label>
                  <div className="space-y-3">
                    {formData.image_url_mobile && (
                      <div className="relative w-48 h-60 bg-muted rounded-lg overflow-hidden mx-auto">
                        <img
                          src={formData.image_url_mobile}
                          alt="Preview Mobile"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage('mobile')}
                          className="absolute top-2 right-2 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        id="image-upload-mobile"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'mobile')}
                        disabled={isUploadingMobile}
                        className="hidden"
                      />
                      <label
                        htmlFor="image-upload-mobile"
                        className={`flex items-center justify-center gap-2 px-4 py-2 border border-border rounded-lg cursor-pointer hover:bg-muted transition-colors ${
                          isUploadingMobile ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        <Upload className="w-4 h-4" />
                        {isUploadingMobile ? "Enviando..." : "Escolher imagem mobile"}
                      </label>
                      {formData.image_url_mobile && (
                        <span className="text-xs text-muted-foreground">
                          Imagem configurada
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Tamanho máximo: 10MB. Formatos: JPG, PNG, WebP
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="admin-label">Texto do Botão</label>
                  <input
                    type="text"
                    value={formData.button_text}
                    onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                    className="admin-input"
                  />
                </div>
                <div>
                  <label className="admin-label">Link do Botão</label>
                  <input
                    type="text"
                    value={formData.button_link}
                    onChange={(e) => setFormData({ ...formData, button_link: e.target.value })}
                    className="admin-input"
                  />
                </div>
              </div>
              <div>
                <label className="admin-label">Texto WhatsApp</label>
                <input
                  type="text"
                  value={formData.whatsapp_text}
                  onChange={(e) => setFormData({ ...formData, whatsapp_text: e.target.value })}
                  className="admin-input"
                />
              </div>
              <div>
                <label className="admin-label">Ordem de exibição</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) =>
                    setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })
                  }
                  className="admin-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active" className="text-sm text-foreground">
                  Slide ativo (visível no carrossel)
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeDialog} className="flex-1 btn-secondary">
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Salvando..."
                    : "Salvar"}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </AdminLayout>
    </AuthGuard>
  );
};

export default AdminCarousel;
